import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Box, Typography, Divider } from '@mui/material';
import { ContentCut, CalendarToday, AccessTime, ArrowBack, PersonOutline, ArrowForwardIos } from '@mui/icons-material';
import NavBar from '../../components/NavBar';
import { colors, typography, radius } from '../../theme';

function formatTime(minutes) {
    if (!minutes && minutes !== 0) return '—';
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-MY', { weekday:'short', day:'numeric', month:'short', year:'numeric' });
}

const STATUS_CONFIG = {
    waiting:   { label:'Waiting',   bg: colors.warningBg,  border: colors.warningBorder,  color: colors.primary, icon:'🕐' },
    serving:   { label:'Serving',   bg: colors.successBg,  border: colors.successBorder,  color: colors.success, icon:'✂️' },
    completed: { label:'Done',      bg: colors.infoBg,     border: colors.infoBorder,     color: colors.info,    icon:'✅' },
    cancelled: { label:'Cancelled', bg: colors.errorBg,    border: colors.errorBorder,    color: colors.error,   icon:'❌' },
    pending:   { label:'Pending',   bg: colors.warningBg,  border: colors.warningBorder,  color: colors.primary, icon:'🕐' },
};

export const BookingCard = ({ booking, onOpenTicket }) => {
    const s        = STATUS_CONFIG[booking.status] ?? STATUS_CONFIG.pending;
    const isActive = ['waiting','serving','pending'].includes(booking.status);
    const isWalkin = booking.type === 'walkin';

    return (
        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${isActive ? `${colors.primary}44` : colors.border}`, borderRadius: radius.lg, overflow:'hidden', position:'relative' }}>
            {isActive && <Box sx={{ position:'absolute', top:0, left:0, right:0, height:2, bgcolor: colors.primary }} />}
            <Box sx={{ p:2, display:'flex', flexDirection:'column', gap:0 }}>

                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:1.5 }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                        <Box sx={{ width:36, height:36, borderRadius:'50%', bgcolor: colors.bg, border:`1px solid ${colors.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {isWalkin
                                ? <PersonOutline sx={{ fontSize:18, color: colors.textMuted }} />
                                : <ContentCut sx={{ fontSize:16, color: colors.primary }} />}
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize:14, fontWeight:600, color: colors.textPrimary, lineHeight:1.2 }}>{booking.barberName ?? 'Barber'}</Typography>
                            <Typography sx={{ fontSize:11, color: colors.textFaint }}>{isWalkin ? 'Walk-in' : 'Appointment'}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ px:1.2, py:0.3, borderRadius:1.5, bgcolor:s.bg, border:`1px solid ${s.border}`, display:'flex', alignItems:'center', gap:0.5 }}>
                        <Typography sx={{ fontSize:11 }}>{s.icon}</Typography>
                        <Typography sx={{ fontSize:11, color:s.color, fontWeight:600 }}>{s.label}</Typography>
                    </Box>
                </Box>

                <Divider sx={{ borderColor: colors.border, mb:1.5 }} />

                <Box sx={{ display:'flex', gap:2.5, flexWrap:'wrap' }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <CalendarToday sx={{ fontSize:12, color: colors.textFaintest }} />
                        <Typography sx={{ fontSize:12, color: colors.textMuted }}>{formatDate(booking.scheduledAt)}</Typography>
                    </Box>
                    <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <AccessTime sx={{ fontSize:12, color: colors.textFaintest }} />
                        <Typography sx={{ fontSize:12, color: colors.textMuted }}>{formatTime(booking.slotMinutes)}</Typography>
                    </Box>
                    {booking.service && (
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                            <ContentCut sx={{ fontSize:12, color: colors.textFaintest }} />
                            <Typography sx={{ fontSize:12, color: colors.textMuted }}>{booking.service}</Typography>
                        </Box>
                    )}
                </Box>

                {isWalkin && booking.queueNumber && (
                    <Box sx={{ mt:1.5, display:'inline-flex', alignItems:'center', gap:1, bgcolor: colors.bg, borderRadius:1.5, px:1.2, py:0.5, border:`1px solid ${colors.border}` }}>
                        <Typography sx={{ fontSize:11, color: colors.textFaint }}>Queue</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize:15, fontWeight:700, color: colors.primary }}>#{booking.queueNumber}</Typography>
                    </Box>
                )}

                {isWalkin && isActive && (
                    <Box onClick={(e) => { e.stopPropagation(); onOpenTicket(booking); }} sx={{ mt:1.5, display:'flex', alignItems:'center', justifyContent:'center', gap:0.5, py:0.9, borderRadius:1.5, bgcolor: colors.primaryDim, border:`1px solid ${colors.primaryBorder}`, cursor:'pointer', transition:'all .2s', '&:hover':{ bgcolor:`${colors.primary}30`, borderColor: colors.primary } }}>
                        <Typography sx={{ fontSize:12, color: colors.primary, fontWeight:600 }}>📋 View Live Queue Position</Typography>
                        <ArrowForwardIos sx={{ fontSize:10, color: colors.primary }} />
                    </Box>
                )}

                <Typography sx={{ fontSize:9, color: colors.border, mt:1.2, fontFamily:'monospace' }}>{booking.id}</Typography>
            </Box>
        </Box>
    );
};

const UserBookings = () => {
    const navigate = useNavigate();
    const [bookings,  setBookings]  = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [userId,    setUserId]    = useState(null);
    const [activeTab, setActiveTab] = useState('upcoming');

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { if (!user) { navigate('/signin'); return; } setUserId(user.uid); });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, 'bookings'), where('userId', '==', userId));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.scheduledAt?.toDate?.() ?? new Date(0)) - (a.scheduledAt?.toDate?.() ?? new Date(0)));
            setBookings(data); setLoading(false);
        }, (err) => { console.error(err); setLoading(false); });
        return () => unsub();
    }, [userId]);

    const today    = new Date(); today.setHours(0,0,0,0);
    const upcoming = bookings.filter(b => { const d = b.scheduledAt?.toDate?.() ?? new Date(0); return d >= today && b.status !== 'cancelled' && b.status !== 'completed'; });
    const past     = bookings.filter(b => { const d = b.scheduledAt?.toDate?.() ?? new Date(0); return d < today || b.status === 'completed' || b.status === 'cancelled'; });
    const handleOpenTicket = (booking) => navigate(`/queue/${booking.shopId}/${booking.id}`);
    const displayList = activeTab === 'upcoming' ? upcoming : past;

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight:'100vh', color: colors.textPrimary, pb:8 }}>
            <NavBar showSearch={false} />

            <Box sx={{ px:3, pt:3, pb:3, borderBottom:`1px solid ${colors.border}` }}>
                <Box onClick={() => navigate(-1)} sx={{ display:'inline-flex', alignItems:'center', gap:0.5, color: colors.textMuted, cursor:'pointer', mb:2, '&:hover':{ color: colors.primary }, transition:'color .2s' }}>
                    <ArrowBack sx={{ fontSize:16 }} />
                    <Typography sx={{ fontSize:13 }}>Back</Typography>
                </Box>
                <Typography sx={{ fontFamily: typography.poppins, fontSize:26, fontWeight:700, color: colors.textPrimary }}>My Bookings</Typography>
                <Typography sx={{ fontSize:13, color: colors.textFaint, mt:0.5 }}>Your appointments and walk-ins</Typography>
            </Box>

            <Box sx={{ display:'flex', px:3, mt:2.5, gap:1 }}>
                {[{ key:'upcoming', label:`Upcoming (${upcoming.length})` }, { key:'past', label:`Past (${past.length})` }].map(tab => (
                    <Box key={tab.key} onClick={() => setActiveTab(tab.key)} sx={{ px:2, py:0.8, borderRadius: radius.md, cursor:'pointer', bgcolor: activeTab === tab.key ? colors.primary : colors.bgCard, border:`1px solid ${activeTab === tab.key ? colors.primary : colors.border}`, transition:'all .2s' }}>
                        <Typography sx={{ fontSize:12, fontWeight:600, color: activeTab === tab.key ? colors.bg : colors.textMuted }}>{tab.label}</Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ px:3, mt:2.5, display:'flex', flexDirection:'column', gap:2 }}>
                {loading ? (
                    [1,2,3].map(i => <Box key={i} sx={{ bgcolor: colors.bgCard, borderRadius: radius.lg, height:140, opacity:0.4 }} />)
                ) : displayList.length === 0 ? (
                    <Box sx={{ textAlign:'center', py:8 }}>
                        <Typography sx={{ fontSize:36, mb:2 }}>{activeTab === 'upcoming' ? '📅' : '✂️'}</Typography>
                        <Typography sx={{ fontSize:16, color: colors.textFaint }}>{activeTab === 'upcoming' ? 'No upcoming bookings' : 'No past bookings'}</Typography>
                        {activeTab === 'upcoming' && <Typography onClick={() => navigate('/user/dashboard')} sx={{ fontSize:13, color: colors.primary, mt:1, cursor:'pointer', '&:hover':{ textDecoration:'underline' } }}>Find a barber →</Typography>}
                    </Box>
                ) : displayList.map(booking => <BookingCard key={booking.id} booking={booking} onOpenTicket={handleOpenTicket} />)}
            </Box>
        </Box>
    );
};

export default UserBookings;